import { useEffect, useRef } from 'react';

import { LogRequest, logCreate } from '../../../@api-client';
import { getFromLocalStorage } from '../../../utils/get-from-local-storage';

export const STORAGE_KEY = 'trackingEvents';
const INTERVAL_MS = 5000;
const BATCH_SIZE = 10;

function readEvents(): LogRequest[] {
  return getFromLocalStorage<LogRequest[]>(STORAGE_KEY, []);
}
function writeEvents(events: LogRequest[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
}

async function postEvents(events: LogRequest[]) {
  const req = await logCreate({
    body: events,
  });

  if (!req.response?.ok) throw new Error(`track POST failed ${req.response?.status}`);
}

export function useTrackingFlusher() {
  const inFlight = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const maybeFlush = async (reason: 'interval' | 'threshold' | 'storage' | 'final' | 'mount') => {
      if (inFlight.current) return;

      const events = readEvents();
      if (events.length === 0) return;

      const shouldSend = reason === 'final' || reason === 'interval' || events.length >= BATCH_SIZE;

      if (!shouldSend) return;

      if (reason === 'interval' && document.visibilityState === 'hidden') return;

      inFlight.current = true;
      try {
        const payload = events.slice();

        await postEvents(payload);

        const eventsCurr = readEvents();

        const remainingEvents = eventsCurr.filter(
          (ev) => !payload.some((p) => p.timestamp === ev.timestamp),
        );

        writeEvents(remainingEvents);
      } catch {
        // Keep events, will retry next tick or next trigger.
      } finally {
        inFlight.current = false;
      }
    };

    const id = window.setInterval(() => void maybeFlush('interval'), INTERVAL_MS);

    const onLocalUpdated = (ev: Event) => {
      const key = (ev as CustomEvent<string>).detail;
      if (key === STORAGE_KEY) void maybeFlush('threshold');
    };
    window.addEventListener('tracking:updated', onLocalUpdated);

    const onStorage = (ev: StorageEvent) => {
      if (ev.key === STORAGE_KEY) void maybeFlush('storage');
    };
    window.addEventListener('storage', onStorage);

    const onVis = () => {
      if (document.visibilityState === 'hidden') void maybeFlush('final');
    };
    const onBeforeUnload = () => void maybeFlush('final');
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('beforeunload', onBeforeUnload);

    void maybeFlush('mount');

    return () => {
      clearInterval(id);
      window.removeEventListener('tracking:updated', onLocalUpdated);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);
}
